import React, { useCallback, useState } from 'react'

import { selectedRect } from '@tiptap/pm/tables'
import { BubbleMenu as BaseBubbleMenu } from '@tiptap/react'

import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'
import { Toggle } from '@tribeplatform/react-ui-kit/Toggle'

import { T } from '../../../../../../i18n/components/T.js'
import { hasHeaderColumn } from '../../../utils.js'
import { MenuItem } from '../../ui/MenuItem.js'
import { Surface } from '../../ui/Surface.js'
import { MenuProps, ShouldShowProps } from '../types.js'

import { isColumnGripSelected } from './utils.js'

export const TableColumnMenu = React.memo(
  ({ editor, appendTo }: MenuProps): JSX.Element => {
    const [hasHeader, setHasHeader] = useState(false)
    const [firstCol, setFirstCol] = useState(false)
    const shouldShow = useCallback(
      ({ view, state, from }: ShouldShowProps) => {
        if (!state) {
          return false
        }

        const selected = isColumnGripSelected({
          editor,
          view,
          state,
          from: from || 0,
        })

        if (!selected) {
          return selected
        }

        setHasHeader(hasHeaderColumn(editor.view.state.selection))

        const rect = selectedRect(state)
        const { left, top } = rect

        if (left === 0 && top === 0) {
          setFirstCol(true)
        } else {
          setFirstCol(false)
        }

        return selected
      },
      [editor],
    )

    const onAddColumnBefore = useCallback(() => {
      editor.chain().addColumnBefore().run()
    }, [editor])

    const onAddColumnAfter = useCallback(() => {
      editor.chain().addColumnAfter().run()
    }, [editor])

    const onDeleteColumn = useCallback(() => {
      editor.chain().deleteColumn().run()
    }, [editor])

    const onToggleHeaderColumn = useCallback(() => {
      editor.chain().toggleHeaderColumn().run()
    }, [editor])

    return (
      <BaseBubbleMenu
        editor={editor}
        pluginKey="tableColumnMenu"
        updateDelay={0}
        tippyOptions={{
          appendTo: () => {
            return appendTo?.current
          },
          offset: [0, 15],
          popperOptions: {
            modifiers: [{ name: 'flip', enabled: false }],
          },
        }}
        shouldShow={shouldShow}
      >
        <Surface className="text-content w-56 inline-flex h-full leading-none gap-0.5 py-6 px-2">
          <List className="w-full" direction="vertical" spacing="lg">
            {firstCol && (
              <MenuItem iconComponent={<SvgIcon name="columns-3-active-1" />}>
                <Toggle
                  size="sm"
                  checked={hasHeader}
                  labelClassName="text-sm"
                  groupClassName="w-full items-center"
                  title={
                    <T
                      id="Generics.HeaderColumn"
                      defaultMessage="Header Column"
                    />
                  }
                  onChange={onToggleHeaderColumn}
                />
              </MenuItem>
            )}
            {!firstCol && (
              <MenuItem
                iconComponent={<SvgIcon name="arrow-left" />}
                onClick={onAddColumnBefore}
              >
                <T id="Generics.InsertLeft" defaultMessage="Insert Left" />
              </MenuItem>
            )}
            <MenuItem
              iconComponent={<SvgIcon name="arrow-right" />}
              onClick={onAddColumnAfter}
            >
              <T id="Generics.InsertRight" defaultMessage="Insert Right" />
            </MenuItem>
            <MenuItem
              iconComponent={<SvgIcon name="trash" />}
              onClick={onDeleteColumn}
            >
              <T id="Generics.Delete" defaultMessage="Delete" />
            </MenuItem>
          </List>
        </Surface>
      </BaseBubbleMenu>
    )
  },
)

TableColumnMenu.displayName = 'TableColumnMenu'
